<template>
  <div class="dash">
    <!-- Top Bar -->
    <div class="topbar">
      <div class="brand">
        <div class="brand-icon"><i class="fas fa-tools"></i> </div>
        <div>
          <div class="brand-name">Dashboard<span> | FvOpex</span></div>
        </div>
      </div>
    </div>

    <div class="main">

      <div v-if="loading" class="loading-overlay">
        <i class="fa fa-spinner spin" style="font-size:32px; color:#4f46e5;"></i>
        <p style="margin-top:8px; color:#5F5E5A; font-size:13px;">Loading dashboard…</p>
      </div>

      <template v-else>

        <!-- Overview KPIs — real data from stats -->
        <template v-if="!isRequesterOnly">
          <div class="section-label">Overview</div>
          <div class="kpi-row">
            <div class="kpi" style="cursor:pointer;" @click="goToRoute('/asset/list')">
              <div class="kpi-top">
                <div class="kpi-label">Total Assets</div>
                <div class="kpi-ico blue"><i class="fa fa-server"></i></div>
              </div>
              <div class="kpi-val">{{ stats.totalAssets }}</div>
              <div class="kpi-sub"><span class="ok">Assets in Inventory</span></div>
            </div>

            <div class="kpi" style="cursor:pointer;" @click="goToRoute('/location/list')">
              <div class="kpi-top">
                <div class="kpi-label">Locations</div>
                <div class="kpi-ico teal"><i class="fa fa-map-marker-alt"></i></div>
              </div>
              <div class="kpi-val">{{ stats.totalLocations }}</div>
              <div class="kpi-sub"><span class="ok">Active Locations</span></div>
            </div>

            <div class="kpi" style="cursor:pointer;" @click="goToRoute('/user/list')">
              <div class="kpi-top">
                <div class="kpi-label">Users</div>
                <div class="kpi-ico purple"><i class="fa fa-users"></i></div>
              </div>
              <div class="kpi-val">{{ stats.totalUsers }}</div>
              <div class="kpi-sub"><span class="ok">{{ stats.totalTeams }} Team{{ stats.totalTeams === 1 ? '' : 's'
                  }}</span></div>
            </div>

            <div class="kpi" style="cursor:pointer;" @click="goToRoute('/library/list')">
              <div class="kpi-top">
                <div class="kpi-label">Attachments</div>
                <div class="kpi-ico amber"><i class="fa fa-paperclip"></i></div>
              </div>
              <div class="kpi-val">{{ stats.totalAttachments }}</div>
              <div class="kpi-sub"><span class="ok">Files In Library</span></div>
            </div>
          </div>
        </template>

        <!-- Work Orders KPIs -->
        <!-- Work Orders KPIs -->
        <div class="section-label">Work Orders</div>
        <div class="kpi-row">
          <div class="kpi" style="cursor:pointer;" @click="$router.push({ name: 'AllSchedules' })">
            <div class="kpi-top">
              <div class="kpi-label">Total Work Orders</div>
              <div class="kpi-ico blue"><i class="fa fa-clipboard-list"></i></div>
            </div>
            <div class="kpi-val">{{ stats.totalWorkOrders }}</div>
            <div class="kpi-sub"><span class="ok">All Work Orders</span></div>
          </div>

          <div class="kpi" style="cursor:pointer;"
            @click="$router.push({ name: 'AllSchedules', query: { status: 'started' } })">
            <div class="kpi-top">
              <div class="kpi-label">Started</div>
              <div class="kpi-ico teal"><i class="fa fa-play"></i></div>
            </div>
            <div class="kpi-val">{{ stats.startedWorkOrders }}</div>
            <div class="kpi-sub"><span class="ok">In Progress</span></div>
          </div>

          <div class="kpi" style="cursor:pointer;"
            @click="$router.push({ name: 'AllSchedules', query: { status: 'under_action' } })">
            <div class="kpi-top">
              <div class="kpi-label">Under Action</div>
              <div class="kpi-ico amber"><i class="fa fa-tools"></i></div>
            </div>
            <div class="kpi-val">{{ stats.underActionWorkOrders }}</div>
            <div class="kpi-sub"><span class="warn">Action Required</span></div>
          </div>

          <div class="kpi" style="cursor:pointer;"
            @click="$router.push({ name: 'AllSchedules', query: { status: 'under_verification' } })">
            <div class="kpi-top">
              <div class="kpi-label">Under Verification</div>
              <div class="kpi-ico purple"><i class="fa fa-check-circle"></i></div>
            </div>
            <div class="kpi-val">{{ stats.underVerificationWorkOrders }}</div>
            <div class="kpi-sub"><span class="ok">Pending Verification</span></div>
          </div>

          <div class="kpi" style="cursor:pointer;"
            @click="$router.push({ name: 'AllSchedules', query: { status: 'completed' } })">
            <div class="kpi-top">
              <div class="kpi-label">Completed</div>
              <div class="kpi-ico teal"><i class="fa fa-check-double"></i></div>
            </div>
            <div class="kpi-val">{{ stats.completedWorkOrders }}</div>
            <div class="kpi-sub"><span class="ok">Finished</span></div>
          </div>

          <div class="kpi" style="cursor:pointer;"
            @click="$router.push({ name: 'AllSchedules', query: { status: 'on_hold' } })">
            <div class="kpi-top">
              <div class="kpi-label">On Hold</div>
              <div class="kpi-ico amber"><i class="fa fa-pause"></i></div>
            </div>
            <div class="kpi-val">{{ stats.onHoldWorkOrders }}</div>
            <div class="kpi-sub"><span class="warn">Paused</span></div>
          </div>

          <div class="kpi" style="cursor:pointer;"
            @click="$router.push({ name: 'AllSchedules', query: { status: 'pending' } })">
            <div class="kpi-top">
              <div class="kpi-label">Pending</div>
              <div class="kpi-ico amber"><i class="fa fa-clock"></i></div>
            </div>
            <div class="kpi-val">{{ stats.pendingWorkOrders }}</div>
            <div class="kpi-sub"><span class="warn">Awaiting Start</span></div>
          </div>
        </div>

        <!-- Inventory KPIs -->
        <template v-if="!isRequesterOnly">
          <div class="section-label">Inventory & Procurement</div>
          <div class="kpi-row">
            <div class="kpi" style="cursor:pointer;" @click="goToRoute('/materials/list')">
              <div class="kpi-top">
                <div class="kpi-label">Total Parts (SKUs)</div>
                <div class="kpi-ico blue"><i class="fa fa-boxes"></i></div>
              </div>
              <div class="kpi-val">{{ stats.totalParts }}</div>
              <div class="kpi-sub">
                <span class="ok">${{ formatValue(stats.inventoryValue) }} Total Value</span>
              </div>
            </div>

            <div class="kpi" style="cursor:pointer;" @click="goToRoute('/materials/list')">
              <div class="kpi-top">
                <div class="kpi-label">Low Stock Alerts</div>
                <div class="kpi-ico amber"><i class="fa fa-exclamation-triangle"></i></div>
              </div>
              <div class="kpi-val">{{ stats.lowStockCount }}</div>
              <div class="kpi-sub">
                <span :class="stats.lowStockCount > 0 ? 'warn' : 'ok'">
                  {{ stats.lowStockCount > 0 ? 'items below reorder level' : 'all stock healthy' }}
                </span>
              </div>
              <div class="kpi-bar">
                <div class="kpi-bar-fill"
                  :style="{ width: lowStockPercent + '%', background: stats.lowStockCount > 0 ? '#EF9F27' : '#0F6E56' }">
                </div>
              </div>
            </div>

            <div class="kpi" style="cursor:pointer;" @click="goToRoute('/purchase-orders/list')">
              <div class="kpi-top">
                <div class="kpi-label">Active Purchase Orders</div>
                <div class="kpi-ico purple"><i class="fa fa-file-invoice"></i></div>
              </div>
              <div class="kpi-val">{{ stats.activePurchaseOrders }}</div>
              <div class="kpi-sub">
                <span class="warn">{{ stats.pendingPOs }} pending approval</span>
              </div>
            </div>

            <div class="kpi" style="cursor:pointer;" @click="goToRoute('/materials/list')">
              <div class="kpi-top">
                <div class="kpi-label">Inventory Value</div>
                <div class="kpi-ico teal"><i class="fa fa-chart-bar"></i></div>
              </div>
              <div class="kpi-val">${{ formatValueShort(stats.inventoryValue) }}</div>
              <div class="kpi-sub"><span class="ok">across all materials</span></div>
            </div>
          </div>
        </template>

        <!-- Two-column: Low Stock + POs -->
        <div class="two-col">
          <!-- Low Stock -->
          <div class="card">
            <div class="card-head">
              <h3><i class="fa fa-exclamation-triangle" style="color:#EF9F27;"></i> Low stock — reorder required</h3>
              <!-- <button class="card-btn">Auto-reorder →</button> -->
            </div>
            <div class="stock-list">
              <template v-if="lowStockParts.length > 0">
                <div v-for="part in lowStockParts" :key="part.name" class="stock-item">
                  <div>
                    <div class="stock-name">{{ part.name }}</div>
                    <div class="stock-meta">On hand: <strong>{{ part.stock }}</strong> &nbsp;|&nbsp; Min: {{
                      part.minStock }}</div>
                  </div>
                  <div class="stock-right">
                    <span :class="['stock-badge', getStockSeverity(part)]">
                      {{ getStockSeverity(part) === 'critical' ? 'Critical' : 'Low' }}
                    </span>
                    <div class="mini-bar">
                      <div class="mini-bar-fill" :style="{
                        width: stockFillPercent(part) + '%',
                        background: getStockSeverity(part) === 'critical' ? '#E24B4A' : '#EF9F27'
                      }"></div>
                    </div>
                    <span class="stock-pct">{{ stockFillPercent(part) }}% of min</span>
                  </div>
                </div>
              </template>
              <div v-else class="empty-state">
                <i class="fa fa-check-circle" style="font-size:24px; color:#0F6E56;"></i>
                <br>All stock levels are healthy ✅
              </div>
            </div>
          </div>

          <!-- Recent POs -->
          <div class="card">
            <div class="card-head">
              <h3><i class="fa fa-truck" style="color:#534AB7;"></i> Recent purchase orders</h3>
              <!-- <button class="card-btn">+ New PO →</button> -->
            </div>
            <div class="po-list">
              <template v-if="recentPOs.length > 0">
                <div v-for="po in recentPOs" :key="po.id" class="po-item">
                  <span class="po-num">{{ po.poNumber }}</span>
                  <span class="po-vendor">{{ po.vendor }}</span>
                  <span :class="['po-status', po.status.toLowerCase()]">{{ po.status }}</span>
                </div>
              </template>
              <div v-else class="empty-state">No purchase orders found</div>
            </div>
          </div>
        </div>

        <!-- Charts Row -->
        <div class="charts-row">
          <!-- Parts Consumption -->
          <div class="card chart-wrap">
            <div class="chart-title">
              <i class="fa fa-chart-bar"></i> Top consumable parts (units used)
            </div>
            <div v-if="hasConsumptionData" class="legend-row">
              <span v-for="(item, i) in partsConsumption" :key="item.name" class="leg-item">
                <span class="leg-swatch" :style="{ background: barColors[i % barColors.length] }"></span>
                {{ item.name }}
              </span>
            </div>
            <div class="chart-canvas-wrap">
              <div v-if="!hasConsumptionData" class="empty-state"
                style="height:100%; display:flex; flex-direction:column; align-items:center; justify-content:center;">
                <i class="fa fa-chart-bar" style="font-size:24px; color:#cbd5e1;"></i>
                <br>No consumption data yet
              </div>
              <canvas v-else ref="partsChartCanvas"></canvas>
            </div>
          </div>

          <!-- PO Status Donut -->
          <div class="card chart-wrap">
            <div class="chart-title">
              <i class="fa fa-circle-notch"></i> PO status breakdown
            </div>
            <div class="legend-row">
              <span v-for="(label, i) in poChartLabels" :key="label" class="leg-item">
                <span class="leg-swatch" :style="{ background: poChartColors[i] }"></span>
                {{ label }}: {{ poChartValues[i] }}
              </span>
            </div>
            <div class="chart-canvas-wrap" style="height:180px;">
              <div v-if="recentPOs.length === 0" class="empty-state"
                style="height:100%; display:flex; flex-direction:column; align-items:center; justify-content:center;">
                <i class="fa fa-file-invoice" style="font-size:24px; color:#cbd5e1;"></i>
                <br>No PO data yet
              </div>
              <canvas v-else ref="poChartCanvas"></canvas>
            </div>
          </div>
        </div>

        <!-- Action Strip -->
        <!-- <div class="actions-strip">
          <div class="actions-left">
            <h4><i class="fa fa-lightbulb" style="color:#EF9F27;"></i> Smart maintenance suggestions</h4>
            <p>Actions to streamline inventory and procurement</p>
          </div>
          <div class="actions-btns">
            <button class="act-btn">Generate PM schedule</button>
            <button class="act-btn">Check reorder needs</button>
            <button class="act-btn primary">+ New checklist</button>
          </div>
        </div> -->

        <!-- Suggestions -->
        <!-- <div class="suggest-grid">
          <div class="suggest-item">
            <div class="suggest-ico blue"><i class="fa fa-robot"></i></div>
            <div class="suggest-text">
              <strong>Predictive alerts</strong>
              <span>AI failure prediction for {{ stats.totalAssets }} assets</span>
            </div>
          </div>
          <div class="suggest-item">
            <div class="suggest-ico amber"><i class="fa fa-file-alt"></i></div>
            <div class="suggest-text">
              <strong>Attachments</strong>
              <span>{{ stats.totalAttachments }} files stored across assets</span>
            </div>
          </div>
          <div class="suggest-item">
            <div class="suggest-ico teal"><i class="fa fa-truck"></i></div>
            <div class="suggest-text">
              <strong>PO automation</strong>
              <span>{{ stats.pendingPOs }} POs pending — send reminders</span>
            </div>
          </div>
          <div class="suggest-item">
            <div class="suggest-ico purple"><i class="fa fa-users"></i></div>
            <div class="suggest-text">
              <strong>Team overview</strong>
              <span>{{ stats.totalUsers }} users across {{ stats.totalTeams }} teams</span>
            </div>
          </div>
        </div> -->

      </template>
    </div>

    <div class="dash-footer">
      <i class="fa fa-clock"></i> FvOpex — Inventory · Purchase Orders · Maintenance Analytics
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { form_api_submit } from '@/utils/FormSubmit'
import {
  Chart,
  BarController, BarElement,
  DoughnutController, ArcElement,
  CategoryScale, LinearScale,
  Tooltip, Legend
} from 'chart.js'

Chart.register(BarController, BarElement, DoughnutController, ArcElement, CategoryScale, LinearScale, Tooltip, Legend)

export default {
  name: 'MaintenanceDashboard',

  setup() {
    const router = useRouter()
    const loading = ref(true)
    const partsChartCanvas = ref(null)
    const poChartCanvas = ref(null)

    let partsChartInstance = null
    let poChartInstance = null

    const todayDate = ref(
      new Date().toLocaleDateString('en-GB', {
        weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
      })
    )

    const stats = ref({
      totalParts: 0,
      inventoryValue: 0,
      lowStockCount: 0,
      activePurchaseOrders: 0,
      pendingPOs: 0,
      totalAssets: 0,
      totalLocations: 0,
      totalUsers: 0,
      totalTeams: 0,
      totalAttachments: 0,
      // Work Orders
      totalWorkOrders: 0,
      startedWorkOrders: 0,
      completedWorkOrders: 0,
      underActionWorkOrders: 0,
      underVerificationWorkOrders: 0,
      onHoldWorkOrders: 0,
      pendingWorkOrders: 0,
    })

    const lowStockParts = ref([])
    const recentPOs = ref([])
    const partsConsumption = ref([])
    const isRequesterOnly = computed(() => (localStorage.getItem('role_code') || '').trim().toLowerCase() === 'requester_only_101')

    const barColors = ['#378ADD', '#1D9E75', '#EF9F27', '#534AB7', '#D85A30']
    const poColorMap = {
      pending: '#EF9F27',
      open: '#378ADD',
      shipped: '#1D9E75',
      closed: '#888780',
      approved: '#534AB7',
      rejected: '#E24B4A',
    }

    // ── Computed ───────────────────────────────────────────────────────
    const lowStockPercent = computed(() => {
      if (!stats.value.totalParts) return 0
      return Math.min(100, Math.round((stats.value.lowStockCount / stats.value.totalParts) * 100))
    })

    const hasConsumptionData = computed(() =>
      partsConsumption.value.some(p => p.consumed > 0)
    )

    const poStatusCounts = computed(() =>
      recentPOs.value.reduce((acc, po) => {
        acc[po.status] = (acc[po.status] || 0) + 1
        return acc
      }, {})
    )

    const poChartLabels = computed(() => Object.keys(poStatusCounts.value))
    const poChartValues = computed(() => Object.values(poStatusCounts.value))
    const poChartColors = computed(() =>
      poChartLabels.value.map(l => poColorMap[l.toLowerCase()] || '#378ADD')
    )

    // ── Helpers ────────────────────────────────────────────────────────
    const formatValue = (val) => {
      if (!val) return '0'
      if (val >= 1_000_000) return (val / 1_000_000).toFixed(1) + 'M'
      if (val >= 1_000) return (val / 1_000).toFixed(1) + 'k'
      return String(val)
    }

    const formatValueShort = (val) => {
      if (!val) return '0'
      if (val >= 1_000_000) return (val / 1_000_000).toFixed(1) + 'M'
      if (val >= 1_000) return (val / 1_000).toFixed(0) + 'k'
      return String(val)
    }

    const getStockSeverity = (part) => part.stock < part.minStock * 0.5 ? 'critical' : 'low'
    const stockFillPercent = (part) => Math.min(100, Math.round((part.stock / part.minStock) * 100))
    const goToRoute = (path) => {
      if (!path) return
      router.push(path)
    }

    // ── Charts ─────────────────────────────────────────────────────────
    const renderCharts = () => {
      const textCol = '#5F5E5A'
      const gridCol = 'rgba(136,135,128,0.12)'

      // Parts bar — only render if there's actual consumed data
      if (partsChartCanvas.value && hasConsumptionData.value) {
        if (partsChartInstance) partsChartInstance.destroy()
        const sorted = [...partsConsumption.value].sort((a, b) => b.consumed - a.consumed)

        partsChartInstance = new Chart(partsChartCanvas.value, {
          type: 'bar',
          data: {
            labels: sorted.map(p => p.name),
            datasets: [{
              label: 'Units consumed',
              data: sorted.map(p => p.consumed),
              backgroundColor: barColors.slice(0, sorted.length),
              borderRadius: 6,
              borderSkipped: false
            }]
          },
          options: {
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              x: { ticks: { color: textCol, font: { size: 11 } }, grid: { color: gridCol } },
              y: { ticks: { color: textCol, font: { size: 11 } }, grid: { color: gridCol }, beginAtZero: true }
            }
          }
        })
      }

      // PO donut
      if (poChartCanvas.value && poChartLabels.value.length > 0) {
        if (poChartInstance) poChartInstance.destroy()
        poChartInstance = new Chart(poChartCanvas.value, {
          type: 'doughnut',
          data: {
            labels: poChartLabels.value,
            datasets: [{
              data: poChartValues.value,
              backgroundColor: poChartColors.value,
              borderWidth: 0,
              hoverOffset: 6
            }]
          },
          options: {
            responsive: true, maintainAspectRatio: false,
            cutout: '68%',
            plugins: { legend: { display: false } }
          }
        })
      }
    }

    // ── Fetch ──────────────────────────────────────────────────────────
    const fetchDashboard = async () => {
      loading.value = true

      const result = await form_api_submit({
        url: 'dashboard',
        method: 'GET',
        show_success_toast: false,
        show_error_toast: true,
      })

      if (result.success) {
        const d = result.data
        stats.value = { ...stats.value, ...d.stats }
        lowStockParts.value = d.lowStockParts ?? []
        recentPOs.value = d.recentPOs ?? []
        partsConsumption.value = d.partsConsumption ?? []
      }

      loading.value = false
      await nextTick()
      renderCharts()
    }

    onMounted(fetchDashboard)

    return {
      loading, todayDate, stats,
      lowStockParts, recentPOs, partsConsumption,
      barColors, lowStockPercent, hasConsumptionData,
      poChartLabels, poChartValues, poChartColors,
      formatValue, formatValueShort,
      getStockSeverity, stockFillPercent, goToRoute,
      isRequesterOnly,
      partsChartCanvas, poChartCanvas,
    }
  }
}
</script>
<style scoped>
/* ── Reset ──────────────────────────────────────────────────────────── */
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* ── Root ───────────────────────────────────────────────────────────── */
.dash {
  background: #f4f7fc;
  min-height: 100vh;
  color: #1e293b;
}

/* ── Top Bar ─────────────────────────────────────────────────────────── */
.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1.5rem;
  background: #fff;
  border-bottom: 0.5px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 0.75rem;
  position: sticky;
  top: 0;
  z-index: 20;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #4f46e5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-icon i {
  color: #E6F1FB;
  font-size: 16px;
}

.brand-name {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.brand-name span {
  font-size: 13px;
  font-weight: 400;
  color: #64748b;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.site-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  background: #f8fafc;
  padding: 5px 12px;
  border-radius: 20px;
  border: 0.5px solid #e2e8f0;
}

.notif {
  position: relative;
  cursor: pointer;
}

.notif i {
  font-size: 20px;
  color: #64748b;
}

.notif-dot {
  position: absolute;
  top: -1px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #E24B4A;
  border-radius: 50%;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #B5D4F4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #0C447C;
}

/* ── Info Strip ──────────────────────────────────────────────────────── */
.infostrip {
  background: #f8fafc;
  padding: 6px 1.5rem;
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  font-size: 11px;
  color: #64748b;
  border-bottom: 0.5px solid #e2e8f0;
}

.infostrip span {
  display: flex;
  align-items: center;
  gap: 5px;
}

.ok-text {
  color: #0F6E56 !important;
}

/* ── Main ────────────────────────────────────────────────────────────── */
.main {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* ── Loading ─────────────────────────────────────────────────────────── */
.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

/* ── KPI Row ─────────────────────────────────────────────────────────── */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.kpi {
  background: #fff;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: box-shadow 0.2s;
}

.kpi:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.07);
}

.kpi-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.kpi-label {
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
}

.kpi-ico {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-ico i {
  font-size: 15px;
}

.kpi-ico.blue {
  background: #E6F1FB;
}

.kpi-ico.blue i {
  color: #4f46e5;
}

.kpi-ico.teal {
  background: #E1F5EE;
}

.kpi-ico.teal i {
  color: #0F6E56;
}

.kpi-ico.amber {
  background: #FAEEDA;
}

.kpi-ico.amber i {
  color: #854F0B;
}

.kpi-ico.purple {
  background: #EEEDFE;
}

.kpi-ico.purple i {
  color: #534AB7;
}

.kpi-val {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
  color: #1e293b;
}

.kpi-sub {
  font-size: 11px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.kpi-sub .ok {
  color: #0F6E56;
}

.kpi-sub .warn {
  color: #854F0B;
}

.kpi-sub .danger {
  color: #A32D2D;
}

.kpi-bar {
  height: 3px;
  background: #e2e8f0;
  border-radius: 2px;
  margin-top: 4px;
}

.kpi-bar-fill {
  height: 3px;
  border-radius: 2px;
  transition: width 0.6s ease;
}

/* ── Two Column ──────────────────────────────────────────────────────── */
.two-col {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 1.25rem;
}

@media (max-width: 768px) {
  .two-col {
    grid-template-columns: 1fr;
  }
}

/* ── Card ────────────────────────────────────────────────────────────── */
.card {
  background: #fff;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.card-head {
  padding: 0.85rem 1.1rem;
  border-bottom: 0.5px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.card-head h3 {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 6px;
}

.card-btn {
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 20px;
  cursor: pointer;
  border: 0.5px solid #cbd5e1;
  background: transparent;
  color: #64748b;
  transition: all 0.15s;
}

.card-btn:hover {
  background: #f8fafc;
  border-color: #4f46e5;
  color: #4f46e5;
}

/* ── Stock List ──────────────────────────────────────────────────────── */
.stock-list {
  padding: 0.25rem 0;
}

.stock-item {
  padding: 0.7rem 1.1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 0.5px solid #f1f5f9;
  transition: background 0.15s;
}

.stock-item:last-child {
  border-bottom: none;
}

.stock-item:hover {
  background: #f8fafc;
}

.stock-name {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
}

.stock-meta {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.stock-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.stock-badge {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 20px;
  font-weight: 600;
}

.stock-badge.critical {
  background: #FCEBEB;
  color: #A32D2D;
}

.stock-badge.low {
  background: #FAEEDA;
  color: #854F0B;
}

.stock-pct {
  font-size: 10px;
  color: #94a3b8;
}

.mini-bar {
  width: 60px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
}

.mini-bar-fill {
  height: 4px;
  border-radius: 2px;
  transition: width 0.5s ease;
}

.empty-state {
  padding: 2rem;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.8;
}

/* ── PO List ─────────────────────────────────────────────────────────── */
.po-list {
  padding: 0.25rem 0;
}

.po-item {
  padding: 0.7rem 1.1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 0.5px solid #f1f5f9;
  transition: background 0.15s;
}

.po-item:last-child {
  border-bottom: none;
}

.po-item:hover {
  background: #f8fafc;
}

.po-num {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
}

.po-vendor {
  font-size: 12px;
  color: #64748b;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 0.5rem;
}

.po-status {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 20px;
  font-weight: 600;
  white-space: nowrap;
}

.po-status.open {
  background: #FAEEDA;
  color: #854F0B;
}

.po-status.shipped {
  background: #E1F5EE;
  color: #0F6E56;
}

.po-status.closed {
  background: #f1f5f9;
  color: #64748b;
}

/* ── Charts Row ──────────────────────────────────────────────────────── */
.charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
}

@media (max-width: 768px) {
  .charts-row {
    grid-template-columns: 1fr;
  }
}

.chart-wrap {
  padding: 1rem 1.1rem 1.25rem;
}

.chart-title {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.6rem;
  display: flex;
  align-items: center;
  gap: 6px;
}

.chart-title i {
  font-size: 15px;
  color: #64748b;
}

.legend-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.leg-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #64748b;
}

.leg-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}

.chart-canvas-wrap {
  position: relative;
  width: 100%;
  height: 200px;
}

.chart-canvas-wrap canvas {
  width: 100% !important;
  height: 100% !important;
}

/* ── Action Strip ────────────────────────────────────────────────────── */
.actions-strip {
  background: #f8fafc;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.actions-left h4 {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.actions-left p {
  font-size: 12px;
  color: #64748b;
  margin-top: 3px;
}

.actions-btns {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.act-btn {
  font-size: 12px;
  padding: 6px 16px;
  border-radius: 20px;
  cursor: pointer;
  border: 0.5px solid #cbd5e1;
  background: #fff;
  color: #334155;
  transition: all 0.15s;
  font-weight: 500;
}

.act-btn:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.act-btn.primary {
  background: #4f46e5;
  color: #E6F1FB;
  border-color: transparent;
}

.act-btn.primary:hover {
  background: #0C447C;
}

/* ── Suggestions Grid ────────────────────────────────────────────────── */
.suggest-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.suggest-item {
  background: #fff;
  border: 0.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.85rem;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  transition: box-shadow 0.2s;
}

.suggest-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.suggest-ico {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.suggest-ico i {
  font-size: 14px;
}

.suggest-ico.blue {
  background: #E6F1FB;
}

.suggest-ico.blue i {
  color: #4f46e5;
}

.suggest-ico.amber {
  background: #FAEEDA;
}

.suggest-ico.amber i {
  color: #854F0B;
}

.suggest-ico.teal {
  background: #E1F5EE;
}

.suggest-ico.teal i {
  color: #0F6E56;
}

.suggest-text strong {
  font-size: 12px;
  font-weight: 600;
  display: block;
  color: #1e293b;
}

.suggest-text span {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
  display: block;
}

/* ── Footer ──────────────────────────────────────────────────────────── */
.dash-footer {
  text-align: center;
  font-size: 11px;
  color: #94a3b8;
  padding: 1.25rem;
  border-top: 0.5px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/* ── Spin animation ──────────────────────────────────────────────────── */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}

/* ── Status Colors ──────────────────────────────────────────────────── */
.po-status.pending {
  background: #FAEEDA;
  color: #854F0B;
}

.po-status.approved {
  background: #EEEDFE;
  color: #534AB7;
}

.po-status.rejected {
  background: #FCEBEB;
  color: #A32D2D;
}

.section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: -0.25rem;
}
</style>